import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { TabsContent } from "../components/ui/tabs";
import {
  copyId,
  formatLastLogin,
  isLastLoginOverTwoWeeks,
  truncateIdFirstLast,
} from "../lib/utils";
import { NewUserTrigger } from "./modals/NewUserTrigger";
import type { User } from "global-types";
import { CopyIndicator } from "./CopyIndicator";
import { AlertTriangle } from "lucide-react";

interface UsersProps {
  users: User[];
  accountId?: string;
}

export const Users = ({ users, accountId }: UsersProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  return (
    <TabsContent value="user" className="mt-4 w-min max-w-full mx-auto">
      <div className="mb-8 space-y-4">
        <NewUserTrigger accountId={accountId} />
        {users.length === 0 ? (
          <p>No users found for this account.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <div className="inline-flex items-center gap-1">ID</div>
                </TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>WorkOS Email</TableHead>
                <TableHead>WorkOS ID</TableHead>
                <TableHead>Last Login At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <button
                      onClick={() => {
                        setActiveId(user.id);
                        copyId(user.id);
                      }}
                      className="hover:text-blue-600 transition-colors cursor-pointer inline-flex items-center gap-2"
                      title="Click to copy ID"
                    >
                      {truncateIdFirstLast(user.id)}
                      <CopyIndicator activeId={activeId} thisId={user.id} />
                    </button>
                  </TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>
                    {`${user.first_name} ${user.last_name}`.trim() || "-"}
                  </TableCell>
                  <TableCell>{user.email || "-"}</TableCell>
                  <TableCell>{user.workos_email || "-"}</TableCell>
                  <TableCell>{user.workos_id || "-"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{formatLastLogin(user.last_login_at)}</span>

                      {isLastLoginOverTwoWeeks(user.last_login_at) && (
                        <div title="Last login is more than 2 weeks ago">
                          <AlertTriangle className="w-4 h-4 text-yellow-500 cursor-help" />
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </TabsContent>
  );
};
